import {
	infoModeName,
	arrangeModeName,
	insolationModeName,
	stylesModeName,
	hotSpotModeName,
	isometricModeName,
	editModeName,
	planModeName,
} from '../context/contextStrings';

/**
 * Configuration for all ApartmentView modes
 * Used across multiple components: CompartmentsList, PlanButtons, PlanViewer, etc.
 *
 * Each mode configuration includes:
 * - UI properties (title, icon, button labels)
 * - Behavior flags (interactive, collapsible, etc.)
 * - Compartments display settings
 * - Click/hover behaviors
 * - Conditional visibility based on project setup
 */

export const MODE_CONFIG = {
	// ==================== INFO MODE ====================
	[infoModeName]: {
		// UI Properties
		// title: 'compartments_areas',
		title: 'compartments_list',
		buttonLabel: 'info',
		buttonTooltip: 'show_apartment_info',
		icon: null, // Uses default icon

		// Compartments List Settings
		compartmentsList: {
			show: true,
			collapsible: true,
			defaultOpen: true,
			showAllCompartments: true,
			valueType: 'area', // What to display: 'area', 'insolation', 'versions', 'empty'
			showIndoorCompartments: true,
			showOutdoorCompartments: true,
			enableHover: true,
			enableClick: true,
		},

		// Interaction Behavior
		interactions: {
			clickBehavior: 'selectAndExpand', // 'selectAndExpand', 'cycleVersion', 'openModal', 'selectOnly'
			hoverBehavior: 'highlight', // 'highlight', 'none'
			enableSvgInteraction: true, // Does clicking SVG paths work?
		},

		// Plan Viewer Settings
		planViewer: {
			showPlan: true,
			interactive: true,
			showTooltips: true,
			enableRotation: false,
		},

		// Additional Features
		features: {
			showSummary: false, // Show summary at bottom (like arrange mode)
			showController: false, // Show mode-specific controller
			requiresSpecialData: false, // Does mode need special data to work?
		},

		// Availability Check
		isAvailable: (apartmentData, projectSetup) => true, // Always available
	},

	// ==================== ARRANGE MODE ====================
	[arrangeModeName]: {
		// title: 'compartments_arrangements',
		title: 'compartments_list',
		buttonLabel: 'arrange',
		buttonTooltip: 'show_arrangements',
		icon: 'IconChair',

		compartmentsList: {
			show: true,
			collapsible: true,
			defaultOpen: true,
			showAllCompartments: true,
			valueType: 'versions',
			showIndoorCompartments: true,
			showOutdoorCompartments: true,
			enableHover: true,
			enableClick: true,
		},

		interactions: {
			clickBehavior: 'cycleVersion',
			hoverBehavior: 'highlight',
			enableSvgInteraction: true,
		},

		planViewer: {
			showPlan: true,
			interactive: true,
			showTooltips: false,
			enableRotation: false,
		},

		features: {
			showSummary: true, // "Wybrano wariant X spośród Y możliwych"
			showController: false,
			requiresSpecialData: true,
		},

		isAvailable: (apartmentData, projectSetup) => {
			// Check if versioning is enabled in project setup
			if (!projectSetup?.apartments?.compartments_versioning) return false;

			// Check if apartment has compartments with versions > 1
			const versions =
				apartmentData?.compartments?.map((c) => c.versions || 0) || [];
			return versions.some((v) => v > 1);
		},
	},

	// ==================== INSOLATION MODE ====================
	[insolationModeName]: {
		// title: 'compartments_insolation',
		title: 'compartments_list',
		buttonLabel: 'insolation',
		buttonTooltip: 'show_sun_insolation',
		icon: 'IconSun',

		compartmentsList: {
			show: true,
			collapsible: true,
			defaultOpen: true,
			showAllCompartments: true,
			valueType: 'insolation',
			showIndoorCompartments: true,
			showOutdoorCompartments: true,
			enableHover: true,
			enableClick: true,
		},

		interactions: {
			clickBehavior: 'selectOnly',
			hoverBehavior: 'highlight',
			enableSvgInteraction: true,
		},

		planViewer: {
			showPlan: true,
			interactive: true,
			showTooltips: true,
			enableRotation: false,
		},

		features: {
			showSummary: false,
			showController: true, // InsolationController slider
			requiresSpecialData: true,
		},

		isAvailable: (apartmentData, projectSetup) => {
			return projectSetup?.apartments?.floorplan_insolation === true;
		},
	},

	// ==================== HOTSPOT MODE ====================
	[hotSpotModeName]: {
		// title: 'compartments_hot_spots',
		title: 'compartments_list',
		buttonLabel: 'hot_spots',
		buttonTooltip: 'show_ai_views',
		icon: 'IconViewPoint',

		compartmentsList: {
			show: true,
			collapsible: true,
			defaultOpen: true,
			showAllCompartments: false, // Only compartments with hotspots
			filterBy: 'hasHotSpot',
			valueType: 'empty', // No value displayed, just compartment name
			showIndoorCompartments: true,
			showOutdoorCompartments: false,
			enableHover: true,
			enableClick: true,
		},

		interactions: {
			clickBehavior: 'openModal', // Opens HotSpotModal
			hoverBehavior: 'highlight',
			enableSvgInteraction: false, // Clicking plan doesn't do anything in hotspot mode
		},

		planViewer: {
			showPlan: true,
			interactive: false,
			showTooltips: false,
			enableRotation: false,
		},

		features: {
			showSummary: false,
			showController: false,
			requiresSpecialData: true,
		},

		isAvailable: (apartmentData, projectSetup) => {
			if (!projectSetup?.interiors?.render_ai) return false;

			// Check if apartment has cameras with hotspots
			const hasHotSpots = apartmentData?.tiers?.some((tier) =>
				tier.cameras?.some(
					(camera) =>
						camera.name !== 'top_3D' &&
						camera.name !== 'axonometric' &&
						camera.name.includes('id') // TODO: standardize this check
				)
			);
			return hasHotSpots || false;
		},
	},

	// ==================== ISOMETRIC MODE ====================
	[isometricModeName]: {
		title: 'compartments_list',
		buttonLabel: 'isometric',
		buttonTooltip: 'show_isometric_view',
		icon: 'IconIsometric',

		compartmentsList: {
			show: true,
			collapsible: true,
			defaultOpen: true,
			showAllCompartments: true,
			valueType: 'area',
			showIndoorCompartments: true,
			showOutdoorCompartments: true,
			enableHover: true,
			enableClick: true,
		},

		interactions: {
			clickBehavior: 'selectOnly', // TODO: Define specific isometric behavior
			hoverBehavior: 'highlight',
			enableSvgInteraction: false,
		},

		planViewer: {
			showPlan: false, // Shows 3D isometric view instead
			interactive: true,
			showTooltips: false,
			enableRotation: true, // Can rotate isometric view
		},

		features: {
			showSummary: false,
			showController: false, // TODO: Might add rotation controller
			requiresSpecialData: true,
		},

		isAvailable: (apartmentData, projectSetup) => {
			if (!projectSetup?.apartments?.render_360) return false;

			// Check if apartment has axonometric camera
			const hasIsometricCamera = apartmentData?.tiers?.some((tier) =>
				tier.cameras?.some((camera) => camera.name.includes('axonometric'))
			);
			return hasIsometricCamera || false;
		},
	},

	// ==================== STYLES MODE ====================
	[stylesModeName]: {
		// title: 'compartments_areas', // Same as info mode for now
		title: 'compartments_list',
		buttonLabel: 'styles',
		buttonTooltip: 'show_3d_styles',
		icon: null,

		compartmentsList: {
			show: true,
			collapsible: true,
			defaultOpen: true,
			showAllCompartments: true,
			valueType: 'area',
			showIndoorCompartments: true,
			showOutdoorCompartments: true,
			enableHover: true,
			enableClick: true,
		},

		interactions: {
			clickBehavior: 'selectOnly',
			hoverBehavior: 'highlight',
			enableSvgInteraction: true,
		},

		planViewer: {
			showPlan: false, // Shows 3D render instead
			interactive: true,
			showTooltips: false,
			enableRotation: false,
		},

		features: {
			showSummary: false,
			showController: false, // TODO: Might add style selector
			requiresSpecialData: true,
		},

		isAvailable: (apartmentData, projectSetup) => {
			return projectSetup?.apartments?.render_top === true;
		},
	},

	// ==================== EDIT MODE ====================
	[editModeName]: {
		title: 'edit_mode',
		buttonLabel: 'edit',
		buttonTooltip: 'edit_apartment',
		icon: null,

		compartmentsList: {
			show: false, // Usually hidden in edit mode
			collapsible: false,
			defaultOpen: false,
			showAllCompartments: true,
			valueType: 'area',
			showIndoorCompartments: true,
			showOutdoorCompartments: true,
			enableHover: false,
			enableClick: false,
		},

		interactions: {
			clickBehavior: 'none',
			hoverBehavior: 'none',
			enableSvgInteraction: false,
		},

		planViewer: {
			showPlan: true,
			interactive: false,
			showTooltips: false,
			enableRotation: false,
		},

		features: {
			showSummary: false,
			showController: false,
			requiresSpecialData: false,
		},

		isAvailable: (apartmentData, projectSetup) => {
			return projectSetup?.apartments?.editing === true;
		},
	},

	// ==================== PLAN MODE ====================
	[planModeName]: {
		title: 'floor_plan',
		buttonLabel: 'plan',
		buttonTooltip: 'show_floor_plan',
		icon: null,

		compartmentsList: {
			show: false, // Plan mode typically doesn't show compartments list
			collapsible: false,
			defaultOpen: false,
			showAllCompartments: true,
			valueType: 'area',
			showIndoorCompartments: true,
			showOutdoorCompartments: true,
			enableHover: false,
			enableClick: false,
		},

		interactions: {
			clickBehavior: 'none',
			hoverBehavior: 'none',
			enableSvgInteraction: false,
		},

		planViewer: {
			showPlan: true,
			interactive: false,
			showTooltips: false,
			enableRotation: false,
		},

		features: {
			showSummary: false,
			showController: false,
			requiresSpecialData: false,
		},

		isAvailable: (apartmentData, projectSetup) => true, // Always available
	},
};

/**
 * Get configuration for a specific mode
 * @param {string} mode - Mode name constant
 * @returns {Object} Mode configuration object
 */
export const getModeConfig = (mode) => {
	return MODE_CONFIG[mode] || MODE_CONFIG[infoModeName]; // Default to info mode
};

/**
 * Get list of available modes for current apartment and project setup
 * @param {Object} apartmentData - Current apartment data
 * @param {Object} projectSetup - Project setup configuration
 * @returns {Array<string>} Array of available mode names
 */
export const getAvailableModes = (apartmentData, projectSetup) => {
	const modes = Object.keys(MODE_CONFIG);
	return modes.filter((mode) => {
		const config = MODE_CONFIG[mode];
		return config.isAvailable(apartmentData, projectSetup);
	});
};

/**
 * Check if a specific mode is available
 * @param {string} mode - Mode name to check
 * @param {Object} apartmentData - Current apartment data
 * @param {Object} projectSetup - Project setup configuration
 * @returns {boolean}
 */
export const isModeAvailable = (mode, apartmentData, projectSetup) => {
	const config = getModeConfig(mode);
	return config.isAvailable(apartmentData, projectSetup);
};

/**
 * Get compartments list configuration for a mode
 * @param {string} mode - Mode name constant
 * @returns {Object} Compartments list configuration
 */
export const getCompartmentsListConfig = (mode) => {
	const config = getModeConfig(mode);
	return config.compartmentsList;
};

/**
 * Get interaction configuration for a mode
 * @param {string} mode - Mode name constant
 * @returns {Object} Interactions configuration
 */
export const getInteractionsConfig = (mode) => {
	const config = getModeConfig(mode);
	return config.interactions;
};
